/**
 * useAccountEvents - React Query hook for forecast events
 *
 * Fetches and manages events (audit trail + notes) for a forecast account
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getEvents,
  createNote,
  deleteEvent,
  type ForecastEvent,
  type GetEventsParams,
  type ForecastEventType,
} from '../api/events';

// Query key factory
export const eventQueryKeys = {
  all: ['forecastEvents'] as const,
  account: (params: { orgId: number; locationId: number; versionId: number; accountId: number; limit?: number }) =>
    [...eventQueryKeys.all, 'account', params] as const,
  department: (params: { orgId: number; locationId: number; versionId: number; departmentName: string; accountType: string; limit?: number }) =>
    [...eventQueryKeys.all, 'department', params] as const,
  version: (params: { orgId: number; locationId: number; versionId: number }) =>
    [...eventQueryKeys.all, 'version', params] as const,
};

export interface UseAccountEventsParams {
  orgId: number;
  locationId: number;
  versionId: number;
  accountId: number;
  year?: number;
  month?: number;
  types?: ForecastEventType[];
  limit?: number;
  enabled?: boolean;
}

export interface UseAccountEventsReturn {
  events: ForecastEvent[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  addNote: (content: string) => Promise<ForecastEvent>;
  deleteEvent: (eventId: string) => Promise<void>;
  isAddingNote: boolean;
  isDeletingEvent: boolean;
}

/**
 * Hook to fetch and manage events for a specific account
 */
export function useAccountEvents({
  orgId,
  locationId,
  versionId,
  accountId,
  year,
  month,
  types,
  limit = 50,
  enabled = true,
}: UseAccountEventsParams): UseAccountEventsReturn {
  const queryClient = useQueryClient();

  // Query key for this account's events (includes limit to separate caches)
  const queryKey = eventQueryKeys.account({ orgId, locationId, versionId, accountId, limit });

  // Fetch events query
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const params: GetEventsParams = {
        orgId,
        locationId,
        versionId,
        accountId,
        limit,
      };

      if (year !== undefined) params.year = year;
      if (month !== undefined) params.month = month;
      if (types && types.length > 0) params.types = types;

      const response = await getEvents(params);
      return response.events;
    },
    enabled: enabled && !!orgId && !!locationId && !!versionId && !!accountId,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  // Add note mutation
  const addNoteMutation = useMutation({
    mutationFn: async (content: string) => {
      return createNote({
        orgId,
        locationId,
        versionId,
        accountId,
        year,
        month,
        content,
      });
    },
    onSuccess: (newEvent) => {
      // Optimistically add the new event to the cache
      queryClient.setQueryData<ForecastEvent[]>(queryKey, (old) => {
        if (!old) return [newEvent];
        return [newEvent, ...old];
      });
    },
  });

  // Delete event mutation
  const deleteEventMutation = useMutation({
    mutationFn: async (eventId: string) => {
      await deleteEvent({ id: eventId, orgId });
    },
    onSuccess: (_, eventId) => {
      // Remove the event from the cache
      queryClient.setQueryData<ForecastEvent[]>(queryKey, (old) => {
        if (!old) return [];
        return old.filter((e) => e.id !== eventId);
      });
    },
  });

  return {
    events: data ?? [],
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
    addNote: addNoteMutation.mutateAsync,
    deleteEvent: deleteEventMutation.mutateAsync,
    isAddingNote: addNoteMutation.isPending,
    isDeletingEvent: deleteEventMutation.isPending,
  };
}

/**
 * Hook to fetch all events for a version (useful for version-wide audit trail)
 */
export function useVersionEvents({
  orgId,
  locationId,
  versionId,
  types,
  limit = 100,
  enabled = true,
}: {
  orgId: number;
  locationId: number;
  versionId: number;
  types?: ForecastEventType[];
  limit?: number;
  enabled?: boolean;
}) {
  const queryKey = eventQueryKeys.version({ orgId, locationId, versionId });

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const params: GetEventsParams = {
        orgId,
        locationId,
        versionId,
        limit,
      };

      if (types && types.length > 0) params.types = types;

      const response = await getEvents(params);
      return response.events;
    },
    enabled: enabled && !!orgId && !!locationId && !!versionId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  return {
    events: data ?? [],
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
  };
}

/**
 * Hook to fetch and manage events for a specific department
 */
export interface UseDepartmentEventsParams {
  orgId: number;
  locationId: number;
  versionId: number;
  departmentName: string;
  accountType: 'Revenue' | 'Expense';
  year?: number;
  month?: number;
  types?: ForecastEventType[];
  limit?: number;
  enabled?: boolean;
}

export interface UseDepartmentEventsReturn {
  events: ForecastEvent[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  addNote: (content: string) => Promise<ForecastEvent>;
  deleteEvent: (eventId: string) => Promise<void>;
  isAddingNote: boolean;
  isDeletingEvent: boolean;
}

export function useDepartmentEvents({
  orgId,
  locationId,
  versionId,
  departmentName,
  accountType,
  year,
  month,
  types,
  limit = 50,
  enabled = true,
}: UseDepartmentEventsParams): UseDepartmentEventsReturn {
  const queryClient = useQueryClient();

  // Query key for this department's events
  const queryKey = eventQueryKeys.department({ orgId, locationId, versionId, departmentName, accountType, limit });

  // Fetch events query
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const params: GetEventsParams = {
        orgId,
        locationId,
        versionId,
        departmentName,
        limit,
      };

      if (year !== undefined) params.year = year;
      if (month !== undefined) params.month = month;
      if (types && types.length > 0) params.types = types;

      const response = await getEvents(params);
      // Filter by accountType since the API doesn't support it directly
      return response.events.filter(e =>
        !e.data.accountType || e.data.accountType === accountType
      );
    },
    enabled: enabled && !!orgId && !!locationId && !!versionId && !!departmentName,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  });

  // Add note mutation
  const addNoteMutation = useMutation({
    mutationFn: async (content: string) => {
      return createNote({
        orgId,
        locationId,
        versionId,
        departmentName,
        accountType,
        year,
        month,
        content,
      });
    },
    onSuccess: (newEvent) => {
      // Optimistically add the new event to the cache
      queryClient.setQueryData<ForecastEvent[]>(queryKey, (old) => {
        if (!old) return [newEvent];
        return [newEvent, ...old];
      });
      // Also invalidate the version-level events cache
      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.version({ orgId, locationId, versionId }),
      });
    },
  });

  // Delete event mutation
  const deleteEventMutation = useMutation({
    mutationFn: async (eventId: string) => {
      await deleteEvent({ id: eventId, orgId });
    },
    onSuccess: (_, eventId) => {
      // Remove the event from the cache
      queryClient.setQueryData<ForecastEvent[]>(queryKey, (old) => {
        if (!old) return [];
        return old.filter((e) => e.id !== eventId);
      });
      // Also invalidate the version-level events cache
      queryClient.invalidateQueries({
        queryKey: eventQueryKeys.version({ orgId, locationId, versionId }),
      });
    },
  });

  return {
    events: data ?? [],
    isLoading,
    isError,
    error: error as Error | null,
    refetch,
    addNote: addNoteMutation.mutateAsync,
    deleteEvent: deleteEventMutation.mutateAsync,
    isAddingNote: addNoteMutation.isPending,
    isDeletingEvent: deleteEventMutation.isPending,
  };
}

export default useAccountEvents;
